import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    ViewChild,
    ViewEncapsulation,
    computed,
    effect,
    inject,
    input,
    signal,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { ButtonComponent, LabelComponent, RootDirective, TooltipDirective } from '@pure/hive';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, map, take, takeUntil } from 'rxjs/operators';
import { AuthorizationService } from '@pure/authz-authorizer';
import { PlatformClientService } from '@pure/pure1-ui-platform-angular';
import { RecipeCatalogService } from '../../../../workflow/services/recipe-catalog.service';
import { isImpersonating } from '../../../../workflow/util/workator.util';
import { RecipeCatalogDetail } from '../../../../workflow/model/recipe-catalog.model';
import { buildRecipeConnectors, buildRecipeIcons } from '../../../../workflow/recipe-catalog/recipe-icons';
import { RecipeImportDialogComponent } from '../../../../workflow/recipe-catalog/recipe-import-dialog/recipe-import-dialog.component';
import {
    RECOMMENDATION_ACTION_EVENT_TOKEN,
    RecommendationActionEvent,
    WorkflowRecipeRenderInfo,
} from '../../../../data/models/recommendations.models';
import { registerAsDialogPlugin } from '../../services/recommendation-component-registry.service';

/** Stable registry key for this plugin. Used as the lookup name on the corresponding RenderInfo. */
export const WORKFLOW_RECIPE_DIALOG_PLUGIN_NAME = 'WorkflowRecipeDialogPluginComponent';

/**
 * Dialog component for the workflow-recipe (`prorec-eone-workflow-recipe`) recommendation.
 *
 * Lists every recipe the signal recommends — title, description, and the connectors the
 * recipe is built against — and routes the footer actions (Dismiss / Cancel) back to the
 * container via {@link RECOMMENDATION_ACTION_EVENT_TOKEN}.
 *
 * Connector display names are not in the resolver payload — it carries raw IDs — so the
 * icon strip and the "Required Connections" line come from the catalog's own per-manifest detail
 * endpoint (`RecipeCatalogService.getManifestDetail`), the same source the catalog page
 * uses. That endpoint is also what makes "Import Recipe" possible: `RecipeCatalogDetail`
 * is a structural superset of the `RecipeCatalogManifest` the import dialog takes.
 */
@Component({
    selector: 'workflow-recipe-dialog-plugin',
    templateUrl: './workflow-recipe-dialog-plugin.component.html',
    styleUrls: ['./workflow-recipe-dialog-plugin.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    preserveWhitespaces: false,
    imports: [
        ButtonComponent,
        FaIconComponent,
        LabelComponent,
        RecipeImportDialogComponent,
        RootDirective,
        TooltipDirective,
    ],
    encapsulation: ViewEncapsulation.None,
})
export class WorkflowRecipeDialogPluginComponent implements OnDestroy {
    renderInfo = input.required<WorkflowRecipeRenderInfo>();

    readonly RecommendationActionEvent = RecommendationActionEvent;

    @ViewChild(RecipeImportDialogComponent) private readonly importDialog: RecipeImportDialogComponent;

    private readonly destroy$ = new Subject<void>();
    private readonly catalog = inject(RecipeCatalogService);

    /**
     * Catalog detail per manifest id. The resolver payload carries raw connector IDs and
     * no display names, so the icon strip and the "Required Connections" line come from the
     * catalog's own detail endpoint instead — the same source the catalog page uses.
     * Absent while loading, and stays absent if the fetch fails: the recipe still renders
     * with its title and description, just without icons.
     */
    protected readonly details = signal<Record<number, RecipeCatalogDetail>>({});

    /**
     * False until the catalog fetch settles. It is what separates "still loading" from
     * "asked, and this manifest has no detail" — the two look identical in
     * {@link details}, and only the second is worth telling the user about.
     */
    protected readonly detailsFetched = signal(false);

    /**
     * Import is gated exactly as the catalog's detail panel gates it — impersonation and
     * the workflow-automation write permission. The recommendation itself stays visible
     * either way: it still tells the user a recipe exists, and the catalog disables the
     * action rather than hiding the recipe.
     */
    protected readonly isImpersonating = signal(false);
    protected readonly hasWriteWorkflowPermission = signal(false);

    protected readonly importDisabled = computed(() => this.isImpersonating() || !this.hasWriteWorkflowPermission());

    /**
     * Why Import is disabled for one recipe, or '' when it is not.
     *
     * Takes the manifest id because the last case is per recipe: a catalog detail that
     * failed to load disables only its own button. Without that case the button sat
     * disabled with an empty tooltip and the customer had nothing to act on.
     */
    protected importDisabledReason(manifestId: number | null): string {
        if (this.isImpersonating()) {
            return 'Cannot import recipes while impersonating.';
        }
        if (!this.hasWriteWorkflowPermission()) {
            return 'You do not have permission to import recipes.';
        }
        if (this.detailsFetched() && (manifestId == null || !this.details()[manifestId])) {
            return 'Recipe details are unavailable right now.';
        }
        return '';
    }

    private readonly recommendationActionEvent$ = inject<Subject<RecommendationActionEvent>>(
        RECOMMENDATION_ACTION_EVENT_TOKEN,
        { optional: true },
    );

    constructor() {
        isImpersonating(inject(PlatformClientService))
            .pipe(take(1))
            .subscribe(value => this.isImpersonating.set(value));

        inject(AuthorizationService)
            .isAllowed('WORKFLOW_AUTOMATION:workflow_automation:write')
            .pipe(
                take(1),
                // Logged before falling back: denying import is the safe answer to a failed
                // check, but it is not the same event as the customer lacking the
                // permission, and without this the two are indistinguishable.
                catchError(err => {
                    console.error('Error checking workflow_automation:write permission:', err);
                    return of(false);
                }),
            )
            .subscribe(allowed => this.hasWriteWorkflowPermission.set(allowed));

        effect(() => {
            const ids = this.renderInfo()
                .recipes.map(r => r.manifestId)
                .filter((id): id is number => id != null);
            if (!ids.length) {
                return;
            }
            this.detailsFetched.set(false);
            forkJoin(
                ids.map(id =>
                    this.catalog.getManifestDetail(id).pipe(
                        map(detail => [id, detail] as const),
                        // One recipe's detail failing must not blank the others. Logged
                        // rather than swallowed: without the detail, Import is disabled and
                        // the reason lives only here.
                        catchError(err => {
                            console.error('Error fetching recipe catalog detail:', err);
                            return of(null);
                        }),
                    ),
                ),
            )
                .pipe(takeUntil(this.destroy$))
                .subscribe(results => {
                    const next: Record<number, RecipeCatalogDetail> = {};
                    for (const entry of results) {
                        if (entry) {
                            next[entry[0]] = entry[1];
                        }
                    }
                    this.details.set(next);
                    this.detailsFetched.set(true);
                });
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Icon strip for a recipe; empty until its catalog detail arrives. */
    protected iconsFor(manifestId: number | null): { icon: IconDefinition; label: string }[] {
        const detail = manifestId == null ? undefined : this.details()[manifestId];
        return detail ? buildRecipeIcons(detail) : [];
    }

    /**
     * Connector display names for the "Required Connections" line, off the same merged list the
     * icon strip uses so the two cannot disagree about a system connector like Email.
     */
    protected connectorNamesFor(manifestId: number | null): string {
        const detail = manifestId == null ? undefined : this.details()[manifestId];
        return detail
            ? buildRecipeConnectors(detail)
                  .map(c => c.display_name)
                  .join(', ')
            : '';
    }

    /**
     * Opens the catalog's import flow. `RecipeCatalogDetail` is a structural superset of
     * the `RecipeCatalogManifest` the dialog takes, so the detail we already fetched is
     * handed straight over.
     */
    protected importRecipe(manifestId: number | null): void {
        if (this.importDisabled()) {
            return;
        }
        const detail = manifestId == null ? undefined : this.details()[manifestId];
        if (detail) {
            // `true` opts into the v2 flow: a folder picker before the import fires.
            // Without it `open()` imports immediately, which is not what the catalog does
            // and gives the user no chance to choose a destination.
            this.importDialog.open(detail, true);
        }
    }

    handleActionEvent(action: RecommendationActionEvent): void {
        this.recommendationActionEvent$?.next(action);
    }
}

registerAsDialogPlugin(WORKFLOW_RECIPE_DIALOG_PLUGIN_NAME, WorkflowRecipeDialogPluginComponent);
